import { useEffect, useRef, useCallback } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

interface UseSidebarTourOptions {
  enabled: boolean;
  user: any;
  hasActivePlan: boolean;
  sidebarState?: 'expanded' | 'collapsed';
  setSidebarOpen?: (open: boolean) => void;
}

const tourSteps = [
  {
    selector: 'sidebar-trigger',
    title: 'Sidebar Toggle',
    description: 'Click here to expand or collapse the sidebar. This helps you navigate between different sections of the application.',
    side: 'bottom' as const,
    align: 'start' as const,
  },
  {
    selector: 'overview',
    title: 'Overview',
    description: 'View your dashboard and analytics here. Get insights into your messaging activity and performance metrics.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'inbox',
    title: 'Inbox',
    description: 'Manage all your conversations in one place. Reply to messages, track customer interactions, and stay on top of your communications.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'contacts',
    title: 'Contacts',
    description: 'Access and manage your contact list. Organize your customers, add new contacts, and view contact details.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'templates',
    title: 'Templates',
    description: 'Create and manage message templates. Save time by using pre-written messages for common scenarios.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'campaigns',
    title: 'Campaigns',
    description: 'Create and manage marketing campaigns. Send bulk messages, schedule campaigns, and track their performance.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'whatsapp-flows',
    title: 'WhatsApp Forms',
    description: 'Build interactive WhatsApp Forms for customer engagement. Create forms, surveys, and interactive experiences.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'automation',
    title: 'Automation',
    description: 'Set up automated workflows and triggers. Automate your messaging and business processes.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'notifications',
    title: 'Notifications',
    description: 'View and manage your notifications. Stay updated with important alerts and messages.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'business-business-accounts',
    title: 'Business Accounts',
    description: 'Manage your business accounts. Connect and configure your WhatsApp Business accounts.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'business-analytics',
    title: 'Analytics',
    description: 'View detailed analytics and reports. Track your messaging performance, engagement metrics, and business insights.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'business-commerce',
    title: 'Commerce',
    description: 'Manage your e-commerce integration. Connect your store and enable shopping features.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'business-integrations',
    title: 'Integrations',
    description: 'Connect with third-party services. Integrate your favorite tools and platforms.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'business-team-management',
    title: 'Team Management',
    description: 'Manage your team members and their permissions. Add team members, assign roles, and control access.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'settings-settings',
    title: 'Settings',
    description: 'Configure your account settings, preferences, and manage your profile. Customize your experience here.',
    side: 'right' as const,
    align: 'start' as const,
  },
  {
    selector: 'business-account-selector',
    title: 'Business Account Selector',
    description: 'Switch between different business accounts. Click here to view and manage your connected business accounts.',
    side: 'right' as const,
    align: 'start' as const,
  },
];

const TOUR_COMPLETED_KEY = 'sidebar-tour-shown';
const TOUR_CURRENT_STEP_KEY = 'sidebar-tour-current-step';

export function useSidebarTour({ enabled, user, hasActivePlan, sidebarState, setSidebarOpen }: UseSidebarTourOptions) {
  const driverObjRef = useRef<any>(null);
  const hasShownTourRef = useRef(false);
  const clickOutsideHandlerRef = useRef<((e: MouseEvent) => void) | null>(null);
  const keyboardHandlerRef = useRef<((e: KeyboardEvent) => void) | null>(null);
  const isNavigatingRef = useRef(false);

  const saveCurrentStep = useCallback((stepIndex: number) => {
    localStorage.setItem(TOUR_CURRENT_STEP_KEY, stepIndex.toString());
  }, []);

  const getCurrentStep = useCallback(() => {
    const saved = localStorage.getItem(TOUR_CURRENT_STEP_KEY);
    return saved ? parseInt(saved, 10) : 0;
  }, []);

  const clearCurrentStep = useCallback(() => {
    localStorage.removeItem(TOUR_CURRENT_STEP_KEY);
  }, []);

  const stopTour = useCallback((saveStep: boolean = true) => {
    if (driverObjRef.current) {
      if (saveStep) {
        try {
          const activeElement = document.querySelector('.driver-highlighted-element');
          if (activeElement) {
            const allSteps = tourSteps.map(stepDef => 
              document.querySelector(`[data-tour="${stepDef.selector}"]`)
            );
            const stepIndex = allSteps.findIndex(el => el === activeElement);
            if (stepIndex >= 0) {
              saveCurrentStep(stepIndex);
            }
          }
        } catch (e) {
        }
      }
      
      driverObjRef.current.destroy();
      driverObjRef.current = null;
    }
    
    isNavigatingRef.current = false;
    
    if (clickOutsideHandlerRef.current) {
      document.removeEventListener('click', clickOutsideHandlerRef.current);
      clickOutsideHandlerRef.current = null;
    }

    if (keyboardHandlerRef.current) {
      document.removeEventListener('keydown', keyboardHandlerRef.current);
      keyboardHandlerRef.current = null;
    }
  }, [saveCurrentStep]);

  const startTour = useCallback((startFromStep?: number) => {
    if (sidebarState === 'collapsed' && setSidebarOpen) {
      setSidebarOpen(true);
      setTimeout(() => {
        initializeAndStartTour(startFromStep);
      }, 500);
    } else {
      initializeAndStartTour(startFromStep);
    }
  }, [sidebarState, setSidebarOpen]);

  function initializeAndStartTour(startFromStep?: number) {
    const allAvailableSteps = tourSteps
      .map(stepDef => {
        const element = document.querySelector(`[data-tour="${stepDef.selector}"]`);
        if (!element) return null;
        return { stepDef, element };
      })
      .filter(step => step !== null);

    if (allAvailableSteps.length === 0) {
      return;
    }

    const stepToStart = startFromStep !== undefined 
      ? startFromStep 
      : getCurrentStep();
    
    let validStartIndex = 0;
    let currentTourIndex = 0;
    for (let i = 0; i < tourSteps.length; i++) {
      const element = document.querySelector(`[data-tour="${tourSteps[i].selector}"]`);
      if (element) {
        if (currentTourIndex === stepToStart || i === stepToStart) {
          validStartIndex = currentTourIndex;
          break;
        }
        currentTourIndex++;
      }
    }
    
    const validStartStep = Math.min(Math.max(0, validStartIndex), allAvailableSteps.length - 1);
    
    const stepsToShow = allAvailableSteps.slice(validStartStep);
    
    const steps = stepsToShow.map((stepData, index) => {
      const isLastStep = index === stepsToShow.length - 1;
      const stepDef = stepData!.stepDef;
      
      return {
        element: stepData!.element,
        popover: {
          title: stepDef.title,
          description: stepDef.description,
          side: stepDef.side,
          align: stepDef.align,
          closeBtnText: 'Done',
          nextBtnText: 'Next',
          prevBtnText: 'Previous',
          showButtons: isLastStep ? ['previous', 'close'] : ['previous', 'next', 'close'],
        },
      };
    });

    if (driverObjRef.current) {
      driverObjRef.current.destroy();
    }

    if (clickOutsideHandlerRef.current) {
      document.removeEventListener('click', clickOutsideHandlerRef.current);
      clickOutsideHandlerRef.current = null;
    }

    if (keyboardHandlerRef.current) {
      document.removeEventListener('keydown', keyboardHandlerRef.current);
      keyboardHandlerRef.current = null;
    }

    const styleId = 'driver-theme-styles';
    let styleElement = document.getElementById(styleId) as HTMLStyleElement;
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }
    
    styleElement.textContent = `
      .driver-popover {
        background: hsl(var(--popover)) !important;
        border: 1px solid hsl(var(--border)) !important;
        border-radius: 12px !important;
        box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1) !important;
        padding: 1.5rem !important;
        font-family: inherit !important;
        min-width: 300px !important;
        max-width: 400px !important;
      }
      .driver-popover-title {
        color: hsl(var(--foreground)) !important;
        font-weight: 600 !important;
        font-size: 1.125rem !important;
        margin-bottom: 0.5rem !important;
        line-height: 1.4 !important;
      }
      .driver-popover-description {
        color: hsl(var(--muted-foreground)) !important;
        font-size: 0.875rem !important;
        line-height: 1.5 !important;
        margin-bottom: 1.25rem !important;
      }
      .driver-popover-footer {
        display: flex !important;
        gap: 0.5rem !important;
        justify-content: flex-end !important;
        margin-top: 1rem !important;
        padding-top: 1rem !important;
        border-top: 1px solid hsl(var(--border)) !important;
      }
      .driver-popover-footer button {
        padding: 0.5rem 1rem !important;
        border-radius: 8px !important;
        font-size: 0.875rem !important;
        font-weight: 500 !important;
        transition: all 0.2s ease !important;
        cursor: pointer !important;
        border: none !important;
        outline: none !important;
      }
      .driver-popover-footer .driver-prev-btn {
        background: hsl(var(--secondary)) !important;
        color: hsl(var(--secondary-foreground)) !important;
      }
      .driver-popover-footer .driver-prev-btn:hover {
        background: hsl(var(--secondary-hover)) !important;
      }
      .driver-popover-footer .driver-next-btn {
        background: linear-gradient(104.53deg, #08090A 0%, #29292D 66.09%) !important;
        color: white !important;
      }
      .driver-popover-footer .driver-next-btn:hover {
        opacity: 0.9 !important;
        transform: translateY(-1px) !important;
      }
      .driver-popover-footer .driver-close-btn {
        background: linear-gradient(104.53deg, #08090A 0%, #29292D 66.09%) !important;
        color: white !important;
      }
      .driver-popover-footer .driver-close-btn:hover {
        opacity: 0.9 !important;
        transform: translateY(-1px) !important;
      }
      .driver-popover-progress {
        background: hsl(var(--muted)) !important;
        height: 4px !important;
        border-radius: 2px !important;
        margin-bottom: 1rem !important;
      }
      .driver-popover-progress-item {
        background: linear-gradient(104.53deg, #08090A 0%, #29292D 66.09%) !important;
        height: 100% !important;
        border-radius: 2px !important;
        transition: width 0.3s ease !important;
      }
     
      .driver-highlighted-element {
        border-radius: 8px !important;
        box-shadow: 0 0 0 4px rgba(8, 9, 10, 0.1) !important;
      }
    `;

    const driverObj = driver({
      showProgress: true,
      steps: steps as any,
      allowClose: true,
      onDestroyStarted: () => {
        localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
        clearCurrentStep();
        hasShownTourRef.current = true;
        if (clickOutsideHandlerRef.current) {
          document.removeEventListener('click', clickOutsideHandlerRef.current);
          clickOutsideHandlerRef.current = null;
        }

        if (keyboardHandlerRef.current) {
          document.removeEventListener('keydown', keyboardHandlerRef.current);
          keyboardHandlerRef.current = null;
        }

        if (driverObjRef.current) {
          driverObjRef.current.destroy();
          driverObjRef.current = null;
        }
      },
      onDestroyed: () => {
        driverObjRef.current = null;
        isNavigatingRef.current = false;
        
        if (clickOutsideHandlerRef.current) {
          document.removeEventListener('click', clickOutsideHandlerRef.current);
          clickOutsideHandlerRef.current = null;
        }

        if (keyboardHandlerRef.current) {
          document.removeEventListener('keydown', keyboardHandlerRef.current);
          keyboardHandlerRef.current = null;
        }
      },
      onHighlightStarted: (step: any) => {
        const stepIndex = tourSteps.findIndex(stepDef => {
          const element = document.querySelector(`[data-tour="${stepDef.selector}"]`);
          return element === step.element;
        });
        if (stepIndex >= 0) {
          saveCurrentStep(stepIndex);
        }
      },
    });

    driverObjRef.current = driverObj;

    keyboardHandlerRef.current = (e: KeyboardEvent) => {
      if (isNavigatingRef.current) {
        return;
      }

      const activeElement = document.activeElement;
      const isInputFocused = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        (activeElement instanceof HTMLElement && activeElement.isContentEditable)
      );

      if (isInputFocused) {
        return; // Don't interfere with typing
      }

      if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
        
        if (!driverObjRef.current) {
          return;
        }

        isNavigatingRef.current = true;

        try {
          driverObjRef.current.moveNext();
          
          setTimeout(() => {
            isNavigatingRef.current = false;
          }, 300);
        } catch (err) {
          isNavigatingRef.current = false;
        }
      }

      if (e.key === 'Backspace' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
        
        if (!driverObjRef.current) {
          return;
        }

        isNavigatingRef.current = true;

        try {
          driverObjRef.current.movePrevious();
          
          setTimeout(() => {
            isNavigatingRef.current = false;
          }, 300);
        } catch (err) {
          isNavigatingRef.current = false;
        }
      }
    };

    document.addEventListener('keydown', keyboardHandlerRef.current, true);

    clickOutsideHandlerRef.current = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      const isOverlay = target.classList.contains('driver-overlay') || 
                       target.classList.contains('driver-overlay-animated');
      const isPopover = target.closest('.driver-popover');
      const isHighlighted = target.closest('.driver-highlighted-element');
      const isPopoverButton = target.closest('.driver-popover button');
      
      if (isOverlay && !isPopover && !isHighlighted && !isPopoverButton) {
        e.preventDefault();
        e.stopPropagation();
        if (driverObjRef.current) {
          localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
          clearCurrentStep();
          hasShownTourRef.current = true;
          try {
            driverObjRef.current.destroy();
          } catch (err) {
          }
          driverObjRef.current = null;
        }
        if (clickOutsideHandlerRef.current) {
          document.removeEventListener('click', clickOutsideHandlerRef.current);
          clickOutsideHandlerRef.current = null;
        }

        if (keyboardHandlerRef.current) {
          document.removeEventListener('keydown', keyboardHandlerRef.current);
          keyboardHandlerRef.current = null;
        }

        const overlay = document.querySelector('.driver-overlay');
        if (overlay) {
          overlay.remove();
        }
        const popover = document.querySelector('.driver-popover');
        if (popover) {
          popover.remove();
        }
      }
    };

    setTimeout(() => {
      if (clickOutsideHandlerRef.current) {
        document.addEventListener('click', clickOutsideHandlerRef.current, true);
      }
    }, 100);
    
    const mouseDownHandler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isOverlay = target.classList.contains('driver-overlay') || 
                       target.classList.contains('driver-overlay-animated');
      const isPopover = target.closest('.driver-popover');
      const isHighlighted = target.closest('.driver-highlighted-element');
      
      if (isOverlay && !isPopover && !isHighlighted) {
        if (driverObjRef.current) {
          localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
          clearCurrentStep();
          hasShownTourRef.current = true;
          try {
            driverObjRef.current.destroy();
          } catch (err) {
          }
          driverObjRef.current = null;
        }
        const overlay = document.querySelector('.driver-overlay');
        if (overlay) overlay.remove();
        const popover = document.querySelector('.driver-popover');
        if (popover) popover.remove();
        document.removeEventListener('mousedown', mouseDownHandler, true);
      }
    };
    
    setTimeout(() => {
      document.addEventListener('mousedown', mouseDownHandler, true);
    }, 100);

    driverObj.drive();

    const observer = new MutationObserver(() => {
      const popover = document.querySelector('.driver-popover');
      if (popover) {
        const doneButton = Array.from(popover.querySelectorAll('button')).find(
          btn => btn.textContent?.trim().toLowerCase() === 'done' || btn.classList.contains('driver-close-btn')
        );
        if (doneButton) {
          const handleDone = () => {
            if (driverObjRef.current) {
              localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
              clearCurrentStep();
              hasShownTourRef.current = true;
              if (clickOutsideHandlerRef.current) {
                document.removeEventListener('click', clickOutsideHandlerRef.current);
                clickOutsideHandlerRef.current = null;
              }
              if (keyboardHandlerRef.current) {
                document.removeEventListener('keydown', keyboardHandlerRef.current);
                keyboardHandlerRef.current = null;
              }
              try {
                driverObjRef.current.destroy();
              } catch (e) {
              }
              driverObjRef.current = null;
              const overlay = document.querySelector('.driver-overlay');
              if (overlay) overlay.remove();
              const popoverEl = document.querySelector('.driver-popover');
              if (popoverEl) popoverEl.remove();
            }
            observer.disconnect();
          };
          doneButton.addEventListener('click', handleDone, { once: true });
        }
      }
    });
    
    observer.observe(document.body, { childList: true, subtree: true });
    
    const originalDestroy = driverObjRef.current?.destroy;
    if (originalDestroy) {
      driverObjRef.current.destroy = function() {
        observer.disconnect();
        return originalDestroy.call(this);
      };
    }

    hasShownTourRef.current = true;
  }

  useEffect(() => {
    return () => {
      if (driverObjRef.current) {
        driverObjRef.current.destroy();
      }
      if (clickOutsideHandlerRef.current) {
        document.removeEventListener('click', clickOutsideHandlerRef.current);
      }
      if (keyboardHandlerRef.current) {
        document.removeEventListener('keydown', keyboardHandlerRef.current);
      }
    };
  }, []);

  return {
    startTour: () => {
      const savedStep = getCurrentStep();
      startTour(savedStep > 0 ? savedStep : undefined);
    },
    stopTour,
    resetTour: () => {
      localStorage.removeItem(TOUR_COMPLETED_KEY);
      localStorage.removeItem(TOUR_CURRENT_STEP_KEY);
      hasShownTourRef.current = false;
      if (driverObjRef.current) {
        driverObjRef.current.destroy();
        driverObjRef.current = null;
      }
    },
  };
}
